import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * What a list shows when it has nothing in it.
 *
 * ⚠️ **An empty list and a broken one look identical**, and that is the problem this solves. Every
 * list here rendered one line of grey text, which reads the same whether the queue is genuinely
 * clear, a filter excluded everything, or the screen failed to understand what came back. An
 * operator cannot act on "no results"; they can act on "nothing matches this filter — clear it".
 *
 * ⚠️ The action slot is for the way OUT of the state, not for a call to action. On a filtered list
 * that is "clear the filter"; on a truly empty queue there is nothing to offer and none is shown.
 */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon: LucideIcon;
  title: string;

  /** One sentence explaining why it might be empty, when there is a reason worth giving. */
  hint?: string;

  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-surface-elevated">
        <Icon aria-hidden className="size-5 text-muted-foreground" />
      </span>

      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">{title}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>

      {action}
    </div>
  );
}
